class DraftManager {
    constructor(apiService) {
        this.apiService = apiService;
        this.currentDraft = null;
        this.isLiveMode = true;
        this.drafts = [];
        this.listeners = [];
    }

    // Event system for state changes
    addListener(callback) {
        this.listeners.push(callback);
    }

    removeListener(callback) {
        this.listeners = this.listeners.filter(l => l !== callback);
    }

    notifyListeners() {
        this.listeners.forEach(callback => {
            try {
                callback({
                    isLiveMode: this.isLiveMode,
                    currentDraft: this.currentDraft,
                    drafts: this.drafts
                });
            } catch (error) {
                console.error('Error in draft state listener:', error);
            }
        });
    }

    // Draft state management
    setLiveMode() {
        this.isLiveMode = true;
        this.currentDraft = null;
        this.notifyListeners();
    }

    setDraftMode(draftName) {
        this.isLiveMode = false;
        this.currentDraft = draftName;
        this.notifyListeners();
    }

    getCurrentMode() {
        return {
            isLiveMode: this.isLiveMode,
            currentDraft: this.currentDraft
        };
    }

    // Draft CRUD operations
    async fetchDrafts() {
        try {
            const { getDrafts } = await import('./apiService.js');
            this.drafts = await getDrafts();
            this.notifyListeners();
            return this.drafts;
        } catch (error) {
            console.error('Error fetching drafts:', error);
            throw error;
        }
    }

    async createDraft(draftName, year, overwrite = false) {
        try {
            const { saveDraft } = await import('./apiService.js');
            const result = await saveDraft(draftName, year, overwrite);
            await this.fetchDrafts(); // Refresh drafts list
            return result;
        } catch (error) {
            console.error('Error creating draft:', error);
            throw error;
        }
    }

    async createEmptyDraft(draftName, year) {
        try {
            const { createEmptyDraft } = await import('./apiService.js');
            const result = await createEmptyDraft(draftName, year);
            await this.fetchDrafts(); // Refresh drafts list
            return result;
        } catch (error) {
            console.error('Error creating empty draft:', error);
            throw error;
        }
    }

    async loadDraft(draftName, year) {
        try {
            const { loadDraft } = await import('./apiService.js');
            const result = await loadDraft(draftName, year);
            this.setLiveMode(); // Switch to live mode after loading
            return result;
        } catch (error) {
            console.error('Error loading draft:', error);
            throw error;
        }
    }

    async renameDraft(draftName, newName) {
        try {
            const { renameDraft } = await import('./apiService.js');
            const result = await renameDraft(draftName, newName);
            
            // If we're currently viewing the renamed draft, update the current draft name
            if (this.currentDraft === draftName) {
                this.setDraftMode(newName);
            }
            
            await this.fetchDrafts(); // Refresh drafts list
            return result;
        } catch (error) {
            console.error('Error renaming draft:', error);
            throw error;
        }
    }

    async deleteDraft(draftName) {
        try {
            const { deleteDraft } = await import('./apiService.js');
            const result = await deleteDraft(draftName);
            
            // If we're currently viewing the deleted draft, switch to live mode
            if (this.currentDraft === draftName) {
                this.setLiveMode();
            }
            
            await this.fetchDrafts(); // Refresh drafts list
            return result;
        } catch (error) {
            console.error('Error deleting draft:', error);
            throw error;
        }
    }

    // Import operations - NEW: Import into drafts
    async importIntoDraft(draftName, fileData, fileType, year) {
        try {
            const { importIntoDraft } = await import('./apiService.js');
            
            console.log('[DraftManager] Importing into draft:', draftName);
            console.log('[DraftManager] File type:', fileType);
            console.log('[DraftManager] Data type:', typeof fileData);
            console.log('[DraftManager] Data length:', fileData ? fileData.length : 'N/A');

            const result = await importIntoDraft(draftName, fileData, fileType, year);
            
            // Switch to the newly created draft
            this.setDraftMode(draftName);
            await this.fetchDrafts(); // Refresh drafts list
            
            return result;
        } catch (error) {
            console.error('Error importing into draft:', error);
            throw error;
        }
    }

    // Utility methods
    generateDraftName(prefix = 'Import') {
        const timestamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');
        return `${prefix}-${timestamp}`;
    }

    getDraftInfo(draftName) {
        return this.drafts.find(draft => draft.draft_name === draftName);
    }

    hasDraft(draftName) {
        return this.drafts.some(draft => draft.draft_name === draftName);
    }

    // Validation
    validateDraftName(draftName) {
        if (!draftName || typeof draftName !== 'string') {
            throw new Error('Draft name must be a non-empty string');
        }

        if (draftName.trim().length === 0) {
            throw new Error('Draft name cannot be empty');
        }

        if (draftName.length > 100) {
            throw new Error('Draft name must be less than 100 characters');
        }

        // Check for invalid characters
        const invalidChars = /[<>:"/\\|?*\x00-\x1f]/;
        if (invalidChars.test(draftName)) {
            throw new Error('Draft name contains invalid characters');
        }

        return draftName.trim();
    }

    // Publishing - Move draft to live
    async publishDraft(draftName, year) {
        try {
            const result = await this.loadDraft(draftName, year);
            // After successful publish, we could optionally delete the draft
            // await this.deleteDraft(draftName);
            return result;
        } catch (error) {
            console.error('Error publishing draft:', error);
            throw error;
        }
    }

    // State serialization for persistence
    serialize() {
        return {
            currentDraft: this.currentDraft,
            isLiveMode: this.isLiveMode
        };
    }

    deserialize(state) {
        if (state) {
            this.currentDraft = state.currentDraft;
            this.isLiveMode = state.isLiveMode;
            this.notifyListeners();
        }
    }

    // Cleanup
    destroy() {
        this.listeners = [];
        this.currentDraft = null;
        this.isLiveMode = true;
        this.drafts = [];
    }
}

export default DraftManager; 